import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { RefreshCw, HardDrive, Cloud, Activity, AlertTriangle, CheckCircle2, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Pretty-format an MB number with at most 2 decimals.
const fmtMB = (n) => `${(n ?? 0).toFixed(2)} MB`;
const fmtKB = (n) => (n >= 1024 ? `${(n / 1024).toFixed(1)} MB` : `${n.toFixed(0)} KB`);

export default function StorageStats() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [purgingAnalytics, setPurgingAnalytics] = useState(false);
  const [odHealth, setOdHealth] = useState(null);
  const [odChecking, setOdChecking] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  const isSuperUser = user?.role === 'super_user' || user?.role === 'admin';

  const [orphanScan, setOrphanScan] = useState(null);
  const [orphanBusy, setOrphanBusy] = useState(false);
  const [shadowScan, setShadowScan] = useState(null);
  const [shadowBusy, setShadowBusy] = useState(false);
  const scanOrphanDrafts = async () => {
    setOrphanBusy(true);
    try {
      const res = await axios.get(`${API}/admin/orphan-drafts/scan`);
      setOrphanScan(res.data);
      toast.success(`Scan complete — ${res.data.total_orphans} orphan draft(s) found`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Scan failed');
    } finally {
      setOrphanBusy(false);
    }
  };
  const cleanupOrphanDrafts = async () => {
    if (!orphanScan || orphanScan.total_orphans === 0) {
      toast.info('Run a scan first');
      return;
    }
    if (!window.confirm(
      `Delete ${orphanScan.total_orphans} orphan draft(s) across PreCons / Soft Digs / Locates?\n\n` +
      `Only drafts with NO project name, NO job link, NO photos, and NO utilities will be removed.\n\n` +
      `This cannot be undone.`
    )) return;
    setOrphanBusy(true);
    try {
      const res = await axios.post(`${API}/admin/orphan-drafts/cleanup?confirm=true`);
      toast.success(`Deleted ${res.data.total_deleted} orphan draft(s)`);
      setOrphanScan(null);
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cleanup failed');
    } finally {
      setOrphanBusy(false);
    }
  };

  const scanShadowDuplicates = async () => {
    setShadowBusy(true);
    try {
      const res = await axios.get(`${API}/admin/shadow-duplicates/scan`);
      setShadowScan(res.data);
      toast.success(`Scan complete — ${res.data.total_to_delete} duplicate(s) found`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Scan failed');
    } finally {
      setShadowBusy(false);
    }
  };

  const cleanupShadowDuplicates = async () => {
    if (!shadowScan || shadowScan.total_to_delete === 0) {
      toast.info('Run a scan first');
      return;
    }
    if (!window.confirm(
      `Delete ${shadowScan.total_to_delete} shadow-duplicate record(s)?\n\n` +
      `For each burst of records created within 3 minutes for the same user + job, ` +
      `the most complete one (submitted > photos > newest) is KEPT, the rest are removed.\n\n` +
      `This cannot be undone.`
    )) return;
    setShadowBusy(true);
    try {
      const res = await axios.post(`${API}/admin/shadow-duplicates/cleanup?confirm=true`);
      toast.success(`Deleted ${res.data.total_deleted} duplicate(s)`);
      setShadowScan(null);
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cleanup failed');
    } finally {
      setShadowBusy(false);
    }
  };

  const runMigration = async (dryRun = false) => {
    if (!dryRun && !window.confirm(
      'This will upload every inline base64 image from existing PreCons, Soft Digs, and Locates to OneDrive, then replace the embedded data in each document with the OneDrive URL.\n\n' +
      'Existing photos will NOT be deleted from the documents — they will be REPLACED with smaller URL strings.\n\n' +
      'Run a Dry Run first to see what will change. Continue with the live migration?'
    )) return;
    setMigrating(true);
    setMigrationResult(null);
    try {
      const url = `${API}/field-reports/admin/migrate-base64-to-onedrive${dryRun ? '?dry_run=true' : ''}`;
      const res = await axios.post(url);
      setMigrationResult({ ...res.data, ran_at: new Date().toISOString() });
      const s = res.data.summary;
      if (dryRun) {
        toast.success(`Dry run: ${s.found} inline images across ${s.docs_updated || 0} docs would be migrated`);
      } else {
        toast.success(`Migrated ${s.migrated} of ${s.found} images (${s.errors} errors); ${s.docs_updated} docs updated`);
        fetchStats();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Migration request failed');
    } finally {
      setMigrating(false);
    }
  };

  const purgeAnalytics = async () => {
    if (!window.confirm('Delete ALL analytics events? This cannot be undone (events are not currently being written, so this is safe).')) return;
    setPurgingAnalytics(true);
    try {
      const res = await axios.delete(`${API}/analytics/clear?purge_all=true`);
      toast.success(`Purged ${res.data.deleted_count?.toLocaleString() || 0} analytics events`);
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to purge analytics');
    } finally {
      setPurgingAnalytics(false);
    }
  };

  const fetchStats = async (opts = {}) => {
    setLoading(true);
    setError(null);
    try {
      // scan_leaks is opt-in (post-mortem 2026-08-13 perf fix). Default
      // page load skips the regex scan; user hits "Scan for Leaks"
      // button below to run it.
      const params = opts.scanLeaks ? '?scan_leaks=true' : '';
      const res = await axios.get(`${API}/admin/storage-stats${params}`);
      setData(res.data);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to load storage stats';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStats(); }, []);

  const runOneDriveHealthCheck = async () => {
    setOdChecking(true);
    try {
      const res = await axios.get(`${API}/onedrive/healthcheck`);
      setOdHealth(res.data);
      if (res.data.status === 'ok') {
        toast.success(`OneDrive OK • upload ${res.data.upload_ms}ms`);
      } else if (res.data.status === 'disconnected') {
        toast.error('OneDrive is not connected');
      } else {
        toast.error(`OneDrive error: ${res.data.error || 'unknown'}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Health check request failed');
    } finally {
      setOdChecking(false);
    }
  };

  // Auto-run a quiet health check when the page loads so the banner is fresh.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/onedrive/healthcheck`);
        if (!cancelled) setOdHealth(res.data);
      } catch (_) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const healthy = data?.advice === 'Healthy';

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6" data-testid="storage-stats-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-['Barlow_Condensed']">
            Storage Health
          </h1>
          <p className="text-slate-500 mt-1">
            Quick database snapshot. Press refresh anytime to recheck.
          </p>
        </div>
        <Button
          onClick={fetchStats}
          disabled={loading}
          className="bg-slate-900 hover:bg-slate-800 text-white"
          data-testid="refresh-storage-stats-btn"
        >
          {loading
            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Checking…</>
            : <><RefreshCw className="w-4 h-4 mr-2" /> Refresh</>}
        </Button>
      </div>

      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4 text-red-700">{error}</CardContent>
        </Card>
      )}

      {data && (
        <>
          {/* Health verdict banner */}
          <Card className={healthy ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}>
            <CardContent className="p-4 flex items-center gap-3">
              {healthy
                ? <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0" />
                : <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0" />}
              <div>
                <p className={`font-bold ${healthy ? 'text-green-900' : 'text-amber-900'}`}>
                  {healthy ? 'Healthy' : 'Needs a look'}
                </p>
                <p className="text-sm text-slate-700">{data.advice}</p>
              </div>
            </CardContent>
          </Card>

          {/* PreferenceTable list-endpoint watchlist — heads off the
              silent .to_list(1000) drop on Proposals / Companies /
              Opportunities / Jobs / People before it hits users. */}
          {data.list_endpoint_watchlist && (() => {
            const wl = data.list_endpoint_watchlist;
            const hasCritical = wl.has_critical;
            const hasWarn = wl.has_warnings;
            const tone = hasCritical
              ? 'border-red-200 bg-red-50'
              : hasWarn
                ? 'border-amber-200 bg-amber-50'
                : 'border-slate-200 bg-white';
            return (
              <Card className={tone} data-testid="list-endpoint-watchlist-card">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    {hasCritical
                      ? <AlertTriangle className="w-5 h-5 text-red-600" />
                      : hasWarn
                        ? <AlertTriangle className="w-5 h-5 text-amber-600" />
                        : <CheckCircle2 className="w-5 h-5 text-green-600" />}
                    <span>List-endpoint watchlist</span>
                    <span className="text-xs font-normal text-slate-500 ml-auto">
                      Each collection has its own cap, shown in its row
                    </span>
                  </CardTitle>
                  <p className="text-xs text-slate-600">
                    Every collection here is served by an unpaginated list route, each capped at its own limit (shown per row below).
                    Once a collection crosses its cap, rows are silently dropped from the UI.
                  </p>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="list-endpoint-watchlist-table">
                      <thead>
                        <tr className="text-left text-xs uppercase text-slate-500 border-b">
                          <th className="py-2 pr-4">Collection</th>
                          <th className="py-2 pr-4 text-right">Row count</th>
                          <th className="py-2 pr-4 text-right">Headroom to cap</th>
                          <th className="py-2 pr-4">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {wl.rows.map(row => {
                          const badge =
                            row.severity === 'critical'
                              ? { cls: 'bg-red-100 text-red-800', label: 'CRITICAL — near cap' }
                              : row.severity === 'warn'
                                ? { cls: 'bg-amber-100 text-amber-800', label: 'WARN — approaching cap' }
                                : { cls: 'bg-slate-100 text-slate-700', label: 'OK' };
                          return (
                            <tr
                              key={row.collection}
                              className="border-b last:border-b-0"
                              data-testid={`list-endpoint-watchlist-row-${row.collection}`}
                            >
                              <td className="py-2 pr-4 font-mono">{row.collection}</td>
                              <td className="py-2 pr-4 text-right font-mono">{row.count.toLocaleString()}</td>
                              <td className="py-2 pr-4 text-right font-mono text-slate-500">{row.headroom.toLocaleString()}</td>
                              <td className="py-2 pr-4">
                                <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${badge.cls}`}>
                                  {badge.label}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {hasWarn && (
                    <p className="text-xs text-slate-700 mt-3">
                      <strong>Next step when a row flips to WARN/CRITICAL:</strong> add server-side pagination
                      to the corresponding list route in <code className="bg-slate-100 px-1 rounded">backend/routes/</code>
                      {' '}before hitting the cap. The frontend PreferenceTable is already virtualized so
                      only the fetch side is at risk.
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })()}

          {/* Contact / Person data-health snapshot — junk-data counts
              (missing name / email / phone) + any docs carrying a
              test_marker field. Replaces the earlier manual approach
              of querying the DB from a shell. */}
          {data.data_health && (() => {
            const dh = data.data_health;
            const anyFlagged = dh.contacts.flagged || dh.people.flagged;
            const tone = anyFlagged
              ? 'border-amber-200 bg-amber-50'
              : 'border-slate-200 bg-white';
            const renderRow = (label, row, testIdPrefix) => (
              <tr
                key={label}
                className={`border-b last:border-b-0 ${row.flagged ? 'bg-amber-50' : ''}`}
                data-testid={`${testIdPrefix}-row`}
              >
                <td className="py-2 pr-4 font-mono">{label}</td>
                <td className="py-2 pr-4 text-right font-mono">{row.total.toLocaleString()}</td>
                <td className="py-2 pr-4 text-right font-mono text-slate-500">{row.missing_name.toLocaleString()}</td>
                <td className="py-2 pr-4 text-right font-mono text-slate-500">{row.missing_email.toLocaleString()}</td>
                <td className="py-2 pr-4 text-right font-mono text-slate-500">{row.missing_phone.toLocaleString()}</td>
                <td className="py-2 pr-4 text-right font-mono">
                  <span className={row.all_three_missing >= 5 ? 'text-amber-700 font-semibold' : 'text-slate-500'}>
                    {row.all_three_missing.toLocaleString()}
                  </span>
                </td>
                <td className="py-2 pr-4 text-right">
                  {row.with_test_marker > 0 ? (
                    <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-800">
                      {row.with_test_marker.toLocaleString()} — test fixtures present
                    </span>
                  ) : (
                    <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-slate-100 text-slate-700">
                      Clean
                    </span>
                  )}
                </td>
              </tr>
            );
            return (
              <Card className={tone} data-testid="data-health-card">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    {anyFlagged
                      ? <AlertTriangle className="w-5 h-5 text-amber-600" />
                      : <CheckCircle2 className="w-5 h-5 text-green-600" />}
                    <span>Data Health &mdash; Contacts &amp; People</span>
                  </CardTitle>
                  <p className="text-xs text-slate-600">
                    Row-level snapshot of the two contact collections. A real
                    contact would have at least one of name / email / phone;
                    rows missing all three are the strongest junk-data signal.
                    The <code className="bg-slate-100 px-1 rounded">test_marker</code>
                    {' '}column flags any rows carrying an Emergent deploy-test
                    fixture &mdash; those should always read 0 in production.
                  </p>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="data-health-table">
                      <thead>
                        <tr className="text-left text-xs uppercase text-slate-500 border-b">
                          <th className="py-2 pr-4">Collection</th>
                          <th className="py-2 pr-4 text-right">Total rows</th>
                          <th className="py-2 pr-4 text-right">Missing name</th>
                          <th className="py-2 pr-4 text-right">Missing email</th>
                          <th className="py-2 pr-4 text-right">Missing phone</th>
                          <th className="py-2 pr-4 text-right">All three missing</th>
                          <th className="py-2 pr-4 text-right">test_marker</th>
                        </tr>
                      </thead>
                      <tbody>
                        {renderRow('contacts', dh.contacts, 'data-health-contacts')}
                        {renderRow('people', dh.people, 'data-health-people')}
                      </tbody>
                    </table>
                  </div>
                  {anyFlagged && (
                    <p className="text-xs text-slate-700 mt-3">
                      <strong>Flagged rows above.</strong> If{' '}
                      <code className="bg-slate-100 px-1 rounded">test_marker</code> is
                      &gt; 0 in production, that&apos;s Emergent deploy-test
                      fixture leakage and needs a targeted purge. If{' '}
                      <code className="bg-slate-100 px-1 rounded">all three missing</code>{' '}
                      is elevated but{' '}
                      <code className="bg-slate-100 px-1 rounded">test_marker</code> is 0,
                      those are likely legitimate but incomplete records
                      &mdash; review case-by-case, don&apos;t bulk-delete.
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })()}

          {/* OneDrive live status banner */}
          {(() => {
            const h = odHealth;
            const isOk = h && h.status === 'ok';
            const isDc = h && h.status === 'disconnected';
            const tone = !h ? 'border-slate-200 bg-slate-50' : (isOk ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50');
            const icon = !h ? <Cloud className="w-6 h-6 text-slate-500 flex-shrink-0" />
                      : isOk ? <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0" />
                      : <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0" />;
            const title = !h ? 'OneDrive status'
                       : isOk ? `OneDrive healthy${h.user_email ? ` — ${h.user_email}` : ''}`
                       : isDc ? 'OneDrive is NOT connected'
                       : 'OneDrive upload failed';
            return (
              <Card className={tone} data-testid="onedrive-health-banner">
                <CardContent className="p-4 flex items-start gap-3 flex-wrap">
                  {icon}
                  <div className="flex-1 min-w-[200px]">
                    <p className={`font-bold ${isOk ? 'text-green-900' : (h ? 'text-red-900' : 'text-slate-900')}`}>{title}</p>
                    {h?.error && <p className="text-sm text-red-700 mt-1 break-all">{h.error}</p>}
                    {isOk && (
                      <p className="text-xs text-slate-600 mt-1">
                        Probe round-trip: upload {h.upload_ms}ms{h.delete_ms != null ? ` • delete ${h.delete_ms}ms` : ''}
                        {h.last_checked ? ` • checked ${new Date(h.last_checked).toLocaleString()}` : ''}
                      </p>
                    )}
                    {h?.last_fallback_at && (
                      <p className="text-xs text-amber-700 mt-1 flex items-center flex-wrap gap-2" data-testid="onedrive-fallback-warning">
                        <span>
                          ⚠️ Last fallback to Cloudinary: {new Date(h.last_fallback_at).toLocaleString()}
                          {h.last_fallback_filename ? ` (${h.last_fallback_filename})` : ''}
                          {h.last_fallback_error ? ` — reason: ${h.last_fallback_error}` : ''}
                        </span>
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              await axios.post(`${API}/onedrive/clear-fallback-warning`);
                              toast.success('Warning dismissed');
                              runOneDriveHealthCheck();
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || 'Failed to clear warning');
                            }
                          }}
                          className="underline font-semibold hover:text-amber-900"
                          data-testid="clear-fallback-warning-btn"
                        >
                          Dismiss
                        </button>
                      </p>
                    )}
                  </div>
                  <Button
                    onClick={runOneDriveHealthCheck}
                    disabled={odChecking}
                    className="bg-slate-900 hover:bg-slate-800 text-white"
                    data-testid="onedrive-healthcheck-btn"
                  >
                    {odChecking
                      ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Checking…</>
                      : <><Cloud className="w-4 h-4 mr-2" /> Test OneDrive</>}
                  </Button>
                </CardContent>
              </Card>
            );
          })()}

          {/* Top-level numbers */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card data-testid="stat-data-size">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wide">
                  <HardDrive className="w-3.5 h-3.5" /> Data
                </div>
                <p className="text-2xl font-bold mt-1">{fmtMB(data.summary.data_size_mb)}</p>
              </CardContent>
            </Card>
            <Card data-testid="stat-storage-size">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wide">
                  <HardDrive className="w-3.5 h-3.5" /> On Disk
                </div>
                <p className="text-2xl font-bold mt-1">{fmtMB(data.summary.storage_size_mb)}</p>
              </CardContent>
            </Card>
            <Card data-testid="stat-doc-count">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wide">
                  <Activity className="w-3.5 h-3.5" /> Documents
                </div>
                <p className="text-2xl font-bold mt-1">{data.summary.total_documents.toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card data-testid="stat-onedrive">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wide">
                  <Cloud className="w-3.5 h-3.5" /> OneDrive
                </div>
                <p className={`text-2xl font-bold mt-1 ${data.onedrive_connected ? 'text-green-600' : 'text-amber-600'}`}>
                  {data.onedrive_connected ? 'Connected' : 'Not connected'}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Growth Watch — passive reminder that flags collections growing
              fast enough to warrant attention. Threshold-based, projects
              annual growth from the last-24h write rate. Computed
              client-side from existing recent_writes + top_collections
              data; no extra backend call. */}
          {data.recent_writes && (() => {
            const WATCHLIST = {
              time_blocks:      { indexed: false, note: 'Timesheet entries. Index audit pending if growth holds.' },
              tickets_811:      { indexed: true,  note: '811 inbox. Indexes added 2026-06-19; safe to grow.' },
              auth_logs:        { indexed: false, note: 'Auth events. Has retention; should self-cap.' },
              time_events:      { indexed: false, note: 'Live clock-in/out events.' },
              timesheets:       { indexed: false, note: 'Daily timesheet rows.' },
              analytics_events: { indexed: false, note: 'Pageview tracking. Purgeable above.' },
            };
            // Prefer `all_doc_counts` (every collection, added 2026-08-13
            // for exactly this UI) and fall back to the top-15 slice for
            // API responses that predate that field.
            const allCounts = data.all_doc_counts || Object.fromEntries(
              (data.top_collections || []).map(c => [c.name, c.doc_count])
            );
            // Always render every watchlist row, even at 0 writes/day —
            // constant layout beats fluctuating row count. A "+0/day"
            // Healthy row is itself useful signal ("nothing clocked in
            // for 24h"). Iterate the WATCHLIST keys, not recent_writes,
            // so quiet collections don't disappear.
            const rows = Object.keys(WATCHLIST)
              .map(name => {
                const meta = WATCHLIST[name];
                const v = (data.recent_writes || {})[name] || {};
                const docs = allCounts[name] ?? 0;
                const perDay = v.last_24h || 0;
                const perYear = perDay * 365;
                // Bands depend on whether the collection is already
                // index-optimized. Indexed collections only need a
                // "Healthy" / "Indexed — high volume" distinction; they
                // can't go red on volume alone because queries are
                // already O(log n).
                let band = 'green';
                if (meta.indexed) {
                  // Already indexed: just a heads-up if traffic looks
                  // unusually heavy. Never red.
                  if (perYear >= 50000) band = 'indexed-busy';
                } else {
                  if (perYear >= 50000) band = 'red';
                  else if (perYear >= 10000) band = 'amber';
                }
                return { name, docs, perDay, perYear, band, note: meta.note };
              })
              .sort((a, b) => b.perYear - a.perYear);
            if (rows.length === 0) return null;
            const bandColors = {
              green: 'bg-emerald-50 text-emerald-800 border-emerald-200',
              amber: 'bg-amber-50 text-amber-800 border-amber-200',
              red:   'bg-rose-50 text-rose-800 border-rose-200',
              'indexed-busy': 'bg-sky-50 text-sky-800 border-sky-200',
            };
            const bandLabel = {
              green: 'Healthy',
              amber: 'Watch — consider indexing soon',
              red:   'Index now',
              'indexed-busy': 'Indexed — high volume OK',
            };
            return (
              <Card data-testid="growth-watch-card">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-lg font-['Barlow_Condensed']">
                    Growth Watch
                  </CardTitle>
                  <span className="text-xs text-slate-500">
                    Projects 1-year size from last-24h write rate
                  </span>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b">
                        <tr>
                          <th className="text-left px-4 py-2 font-semibold">Collection</th>
                          <th className="text-right px-4 py-2 font-semibold">Now</th>
                          <th className="text-right px-4 py-2 font-semibold">Per day</th>
                          <th className="text-right px-4 py-2 font-semibold">1-yr projection</th>
                          <th className="text-left px-4 py-2 font-semibold">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map(r => (
                          <tr
                            key={r.name}
                            className="border-b border-slate-100"
                            data-testid={`growth-watch-row-${r.name}`}
                          >
                            <td className="px-4 py-2">
                              <div className="font-mono text-xs">{r.name}</div>
                              <div className="text-xs text-slate-500 mt-0.5">{r.note}</div>
                            </td>
                            <td className="px-4 py-2 text-right tabular-nums">
                              {r.docs.toLocaleString()}
                            </td>
                            <td className="px-4 py-2 text-right tabular-nums">
                              +{r.perDay.toLocaleString()}
                            </td>
                            <td className="px-4 py-2 text-right tabular-nums">
                              ~{(r.docs + r.perYear).toLocaleString()}
                            </td>
                            <td className="px-4 py-2">
                              <span
                                className={`inline-block text-xs px-2 py-1 rounded-full border ${bandColors[r.band]}`}
                                data-testid={`growth-watch-status-${r.name}`}
                              >
                                {bandLabel[r.band]}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="px-4 py-2 text-xs text-slate-500 border-t bg-slate-50">
                    Bands: <span className="text-emerald-700">Healthy</span> &lt; 10K/yr ·{' '}
                    <span className="text-amber-700">Watch</span> 10K–50K/yr ·{' '}
                    <span className="text-rose-700">Index now</span> 50K+/yr ·{' '}
                    <span className="text-sky-700">Indexed — high volume OK</span> = already optimized, ignore the projection.
                    A single big sync can spike the 1-yr number; check again after 24 h of normal traffic.
                  </div>
                </CardContent>
              </Card>
            );
          })()}

          {/* Recent activity */}
          {data.recent_writes && Object.keys(data.recent_writes).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-['Barlow_Condensed']">Recent Activity</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="text-left px-4 py-2 font-semibold">Collection</th>
                        <th className="text-right px-4 py-2 font-semibold">Last Hour</th>
                        <th className="text-right px-4 py-2 font-semibold">Last 24 Hours</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(data.recent_writes).map(([name, v]) => (
                        <tr key={name} className="border-b border-slate-100">
                          <td className="px-4 py-2 font-mono text-xs">{name}</td>
                          <td className="px-4 py-2 text-right">{v.last_1h.toLocaleString()}</td>
                          <td className="px-4 py-2 text-right">{v.last_24h.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Top collections */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-['Barlow_Condensed']">Top Collections</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left px-4 py-2 font-semibold">Collection</th>
                      <th className="text-right px-4 py-2 font-semibold">Docs</th>
                      <th className="text-right px-4 py-2 font-semibold">Size</th>
                      <th className="text-right px-4 py-2 font-semibold">Avg / Doc</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.top_collections.map(c => (
                      <tr
                        key={c.name}
                        className={`border-b border-slate-100 ${c.avg_doc_kb > 50 ? 'bg-amber-50' : ''}`}
                      >
                        <td className="px-4 py-2 font-mono text-xs">
                          <div className="flex items-center gap-2">
                            <span>{c.name}</span>
                            {c.name === 'analytics_events' && c.doc_count > 0 && isSuperUser && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={purgeAnalytics}
                                disabled={purgingAnalytics}
                                className="h-6 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid="purge-analytics-btn"
                                title="Delete all analytics events"
                              >
                                {purgingAnalytics
                                  ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                  : <Trash2 className="w-3 h-3 mr-1" />}
                                Purge
                              </Button>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-2 text-right">{c.doc_count.toLocaleString()}</td>
                        <td className="px-4 py-2 text-right">{fmtKB(c.size_kb)}</td>
                        <td className={`px-4 py-2 text-right ${c.avg_doc_kb > 50 ? 'font-bold text-amber-700' : ''}`}>
                          {c.avg_doc_kb > 1024
                            ? `${(c.avg_doc_kb / 1024).toFixed(2)} MB`
                            : `${c.avg_doc_kb.toFixed(2)} KB`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {data.fat_collections.length > 0 && (
                <div className="border-t border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
                  Highlighted rows have an average document size above 50 KB — usually a sign of embedded files.
                  If file uploads should be going to OneDrive, this is worth investigating.
                </div>
              )}
            </CardContent>
          </Card>

          {/* Migrate base64 → OneDrive (always visible to admins) */}
          {isSuperUser && (
            <Card className={data.embedded_image_leaks && Object.keys(data.embedded_image_leaks || {}).length > 0 ? 'border-red-200' : 'border-slate-200'}>
              <CardHeader>
                <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
                  {data.embedded_image_leaks && Object.keys(data.embedded_image_leaks || {}).length > 0
                    ? <AlertTriangle className="w-5 h-5 text-red-600" />
                    : <Cloud className="w-5 h-5 text-slate-600" />}
                  Migrate Embedded Images → OneDrive
                </CardTitle>
              </CardHeader>
              <CardContent>
                {data.leaks_scanned === false || data.embedded_image_leaks === null ? (
                  <div className="mb-4">
                    {data.last_leak_scan ? (
                      <div className="mb-3 text-sm">
                        <div className="flex items-center gap-2 mb-1">
                          {data.last_leak_scan.total_leaks > 0 ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800" data-testid="last-leak-scan-status">
                              <AlertTriangle className="w-3 h-3" />
                              {data.last_leak_scan.total_leaks} leak{data.last_leak_scan.total_leaks === 1 ? '' : 's'} in last scan
                            </span>
                          ) : data.last_leak_scan.status === 'error' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800" data-testid="last-leak-scan-status">
                              Last scan errored
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800" data-testid="last-leak-scan-status">
                              Last scan clean
                            </span>
                          )}
                          <span className="text-xs text-slate-500" data-testid="last-leak-scan-when">
                            {new Date(data.last_leak_scan.finished_at).toLocaleString()} · {data.last_leak_scan.triggered_by}
                          </span>
                        </div>
                        {data.last_leak_scan.total_leaks > 0 && (
                          <ul className="text-xs text-slate-700 ml-1 mb-2 space-y-0.5" data-testid="last-leak-scan-breakdown">
                            {Object.entries(data.last_leak_scan.per_collection || {}).map(([name, n]) => (
                              <li key={name}><span className="font-mono">{name}</span>: <strong>{n}</strong></li>
                            ))}
                          </ul>
                        )}
                        <p className="text-xs text-slate-500">
                          Scheduled daily at 3:45 AM UTC. Super_users are emailed only when the total leak count increases vs the previous scan.
                        </p>
                      </div>
                    ) : (
                      <p className="text-sm text-slate-600 mb-3">
                        Base64-image leak scan is skipped by default (it&apos;s a full-collection
                        regex — slow on large data). A nightly scheduled scan runs at 3:45 AM UTC;
                        results appear here once the first run completes.
                      </p>
                    )}
                    <Button
                      onClick={() => fetchStats({ scanLeaks: true })}
                      disabled={loading}
                      variant="outline"
                      size="sm"
                      data-testid="scan-leaks-btn"
                    >
                      {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <AlertTriangle className="w-4 h-4 mr-2" />}
                      Rescan Now
                    </Button>
                  </div>
                ) : data.embedded_image_leaks && Object.keys(data.embedded_image_leaks).length > 0 ? (
                  <>
                    <p className="text-sm text-slate-700 mb-3">
                      These collections contain documents whose photos are stored as base64 directly in MongoDB
                      instead of OneDrive. This bloats the database.
                    </p>
                    <ul className="text-sm space-y-1 mb-4">
                      {Object.entries(data.embedded_image_leaks).map(([name, n]) => (
                        <li key={name}>
                          <span className="font-mono">{name}</span>: <strong>{n}</strong> documents
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <p className="text-sm text-slate-600 mb-4">
                    Scan complete — no inline base64 detected. Run a <strong>Dry Run</strong> below
                    to double-check with the deeper recursive walk.
                  </p>
                )}
                <div className="border-t border-slate-200 pt-3 space-y-2">
                  <p className="text-sm font-semibold text-slate-800">Migrate base64 → OneDrive</p>
                  <p className="text-xs text-slate-600">
                    Uploads every inline base64 image to OneDrive (or storage fallback) and replaces the document field
                    with the returned URL. Safe to re-run; only base64 strings are touched. Idempotent.
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      onClick={() => runMigration(true)}
                      disabled={migrating}
                      variant="outline"
                      size="sm"
                      data-testid="migrate-dry-run-btn"
                    >
                      {migrating
                        ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Working…</>
                        : <>Dry Run</>}
                    </Button>
                    <Button
                      onClick={() => runMigration(false)}
                      disabled={migrating}
                      className="bg-red-600 hover:bg-red-700 text-white"
                      size="sm"
                      data-testid="migrate-base64-btn"
                    >
                      {migrating
                        ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Migrating…</>
                        : <>Migrate to OneDrive</>}
                    </Button>
                  </div>
                  {migrationResult && (
                    <div className="mt-3 text-xs bg-slate-50 border border-slate-200 rounded p-3 space-y-1">
                      <p className="font-semibold text-slate-800">
                        {migrationResult.dry_run ? 'Dry Run Report' : 'Migration Report'}
                        <span className="text-slate-400 font-normal ml-2">
                          ({new Date(migrationResult.ran_at).toLocaleString()})
                        </span>
                      </p>
                      <p>
                        <strong>{migrationResult.summary.found}</strong> base64 images found,{' '}
                        <strong>{migrationResult.summary.migrated}</strong> migrated,{' '}
                        <strong className="text-red-600">{migrationResult.summary.errors}</strong> errors,{' '}
                        <strong>{migrationResult.summary.docs_updated}</strong> docs updated
                      </p>
                      <p className="text-slate-500">
                        Bytes freed in MongoDB: <strong>{(migrationResult.summary.bytes_freed / 1024 / 1024).toFixed(2)} MB</strong>
                      </p>
                      <details className="mt-2">
                        <summary className="cursor-pointer text-slate-600">Per-collection breakdown</summary>
                        <pre className="mt-1 text-[10px] overflow-auto">
                          {JSON.stringify(migrationResult.by_collection, null, 2)}
                        </pre>
                      </details>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          <p className="text-xs text-slate-400 text-right">
            Snapshot taken {new Date(data.as_of).toLocaleString()}
          </p>

          {/* Orphan Field-Report Drafts Cleanup */}
          {isSuperUser && (
            <Card className="border-orange-200" data-testid="orphan-drafts-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-sm">
                  <Trash2 className="w-4 h-4 text-orange-500" />
                  Orphan Field-Report Drafts
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-3">
                <p className="text-slate-600">
                  Find and delete empty PreCon / Soft Dig / Locate drafts that were created by
                  the historical "save draft" race condition. Only drafts with no project name,
                  no job link, no photos, and no utilities are removed.
                </p>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={scanOrphanDrafts}
                    disabled={orphanBusy}
                    data-testid="scan-orphan-drafts-btn"
                  >
                    {orphanBusy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                    Scan Orphan Drafts
                  </Button>
                  {orphanScan && orphanScan.total_orphans > 0 && (
                    <Button
                      size="sm"
                      className="bg-red-600 hover:bg-red-700"
                      onClick={cleanupOrphanDrafts}
                      disabled={orphanBusy}
                      data-testid="cleanup-orphan-drafts-btn"
                    >
                      {orphanBusy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Trash2 className="w-3 h-3 mr-1" />}
                      Delete {orphanScan.total_orphans} Orphan{orphanScan.total_orphans === 1 ? '' : 's'}
                    </Button>
                  )}
                </div>
                {orphanScan && (
                  <div className="bg-slate-50 rounded p-3 text-xs space-y-1">
                    <p className="font-semibold text-slate-700">
                      Found {orphanScan.total_orphans} orphan draft(s):
                    </p>
                    {Object.entries(orphanScan.collections || {}).map(([k, v]) => (
                      <p key={k} className="text-slate-600">
                        <strong>{k}</strong>: {v.orphan_count || 0} orphan{v.orphan_count === 1 ? '' : 's'} / {v.total_drafts || 0} total draft(s)
                      </p>
                    ))}
                    {orphanScan.total_orphans === 0 && (
                      <p className="text-emerald-700">No orphan drafts — clean.</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Shadow-Duplicate Cleanup — for records created by the retry bug */}
          {isSuperUser && (
            <Card className="border-red-200" data-testid="shadow-duplicates-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-sm">
                  <Trash2 className="w-4 h-4 text-red-500" />
                  Shadow-Duplicate Field Reports
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-3">
                <p className="text-slate-600">
                  Removes the "10 duplicates per submit" bursts caused by the LTE-retry bug
                  (now fixed in code). For every group of records the same user created for
                  the same job within 3 minutes, the most complete one is kept (submitted &gt;
                  has photos &gt; newest) and the rest are deleted.
                </p>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={scanShadowDuplicates}
                    disabled={shadowBusy}
                    data-testid="scan-shadow-duplicates-btn"
                  >
                    {shadowBusy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                    Scan for Duplicate Bursts
                  </Button>
                  {shadowScan && shadowScan.total_to_delete > 0 && (
                    <Button
                      size="sm"
                      className="bg-red-600 hover:bg-red-700"
                      onClick={cleanupShadowDuplicates}
                      disabled={shadowBusy}
                      data-testid="cleanup-shadow-duplicates-btn"
                    >
                      {shadowBusy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Trash2 className="w-3 h-3 mr-1" />}
                      Delete {shadowScan.total_to_delete} Duplicate{shadowScan.total_to_delete === 1 ? '' : 's'}
                    </Button>
                  )}
                </div>
                {shadowScan && (
                  <div className="bg-slate-50 rounded p-3 text-xs space-y-2 max-h-96 overflow-y-auto">
                    <p className="font-semibold text-slate-700">
                      Found {shadowScan.total_to_delete} duplicate(s) across {Object.values(shadowScan.collections || {}).reduce((a, c) => a + ((c.runs && c.runs.length) || 0), 0)} burst(s):
                    </p>
                    {Object.entries(shadowScan.collections || {}).map(([k, v]) => (
                      <div key={k} className="border-l-2 border-slate-300 pl-2 mt-1">
                        <p className="font-semibold text-slate-700 capitalize">{k.replace('_', ' ')}: {v.total_to_delete || 0} to delete</p>
                        {(v.runs || []).slice(0, 10).map((r, i) => (
                          <div key={i} className="text-slate-600 mt-1">
                            <p><strong>{r.project_name || '(no name)'}</strong> — {r.user}</p>
                            <p className="text-emerald-700 pl-2">KEEP: {r.keep.id.slice(0, 8)} ({r.keep.status}, score {r.keep.score})</p>
                            <p className="text-red-600 pl-2">DEL: {r.delete.map(d => `${d.id.slice(0, 8)}(${d.status})`).join(', ')}</p>
                          </div>
                        ))}
                        {(v.runs || []).length > 10 && (
                          <p className="text-slate-500 italic">…and {v.runs.length - 10} more bursts</p>
                        )}
                      </div>
                    ))}
                    {shadowScan.total_to_delete === 0 && (
                      <p className="text-emerald-700">No duplicate bursts — clean.</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
